import { useState, useEffect } from "react";
import { useLenis } from "lenis/react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import AYMark from "./AYMark";

const LINKS = [
  { id: "quidory", label: "Quidory" },
  { id: "media", label: "Media" },
  { id: "services", label: "Capabilities" },
  { id: "ventures", label: "Ventures" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const lenis = useLenis();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, id) => {
    e.preventDefault();
    setOpen(false);
    const target = document.getElementById(id);
    if (target && lenis) lenis.scrollTo(target, { offset: -70 });
    else if (target) target.scrollIntoView({ behavior: "smooth" });
  };

  const goTop = (e) => {
    e.preventDefault();
    setOpen(false);
    if (lenis) lenis.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-ink/80 backdrop-blur-xl border-b border-hairline"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-12">
          <a
            href="#top"
            onClick={goTop}
            data-testid="nav-logo"
            className="flex items-center gap-3"
            aria-label="Al Yakhour LLC home"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-sm border border-hairline bg-surface text-steel-light">
              <AYMark className="h-5 w-5" />
            </span>
            <span className="font-display text-base font-extrabold tracking-tight text-cloud">
              Al Yakhour<span className="text-steel-light"> LLC</span>
            </span>
          </a>

          <div className="hidden items-center gap-9 md:flex">
            {LINKS.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={(e) => go(e, l.id)}
                data-testid={`nav-link-${l.id}`}
                className="font-body text-sm font-medium tracking-tight text-fog transition-colors duration-300 hover:text-cloud"
              >
                {l.label}
              </a>
            ))}
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            className="flex h-10 w-10 items-center justify-center text-cloud md:hidden"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-x-0 top-[65px] z-40 border-b border-hairline bg-ink/95 backdrop-blur-xl md:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col px-6 py-4">
              {LINKS.map((l) => (
                <a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={(e) => go(e, l.id)}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className="border-b border-hairline/60 py-4 font-display text-lg font-medium text-cloud"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
